import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DiffEditor } from '@monaco-editor/react';
import {
  ArrowLeft,
  Play,
  Pause,
  RotateCcw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Star,
  ExternalLink,
  FileCode,
  Eye,
  GitCompare,
  History,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CheckpointReview } from './CheckpointReview';
import { ParetoChart, IterationHistory } from './ParetoChart';
import { OptimizationProgressChart } from './OptimizationProgressChart';
import { TextDiffSummary } from './TextDiffSummary';
import { DirectivesEditor } from './DirectivesEditor';
import { EvalWizard } from './EvalWizard';
import {
  UseEvalDetailReturn,
  OptimizationStatus,
  OptimizationIteration,
} from './hooks/useEvalDetail';
import { getAuthHeaders } from '@/services/authConfig';

interface BestConfigData {
  iteration: number;
  config_snapshot: Record<string, unknown>;
  current_agent_content: string;
  optimized_agent_content: string;
  improvement_percent: number | null;
  objective_values: Record<string, number>;
}

interface IterationConfigData {
  iteration: number;
  is_baseline: boolean;
  is_best: boolean;
  config_snapshot: Record<string, unknown>;
  baseline_content: string;
  iteration_content: string;
  improvement_from_baseline: number | null;
  objective_values: Record<string, number>;
  proposed_changes: Array<{
    change_type?: string;
    type?: string;  // Legacy field name
    field_path?: string;
    rationale?: string;
    description?: string;  // Legacy field name
  }> | null;
}

interface OptimizationEvalDetailProps {
  evalId: string;
  hook: UseEvalDetailReturn;
}

export function OptimizationEvalDetail({
  evalId,
  hook,
}: OptimizationEvalDetailProps) {
  const navigate = useNavigate();
  const {
    evalData,
    optStatus,
    pareto,
    iterations,
    loading,
    startingOptimization,
    error,
    setError,
    fetchOptimizationData,
    optimizationRuns,
    selectedRunId,
    selectOptimizationRun,
    handleStartOptimization,
    handlePauseOptimization,
    handleResumeOptimization,
  } = hook;

  const [checkpointDialogOpen, setCheckpointDialogOpen] = useState(false);
  const [selectedCheckpointId, setSelectedCheckpointId] = useState<string | null>(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [bestConfigData, setBestConfigData] = useState<BestConfigData | null>(null);
  const [loadingBestConfig, setLoadingBestConfig] = useState(false);
  const [applyingConfig, setApplyingConfig] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applySuccess, setApplySuccess] = useState(false);

  // Iteration diff dialog state
  const [iterationDiffOpen, setIterationDiffOpen] = useState(false);
  const [selectedIteration, setSelectedIteration] = useState<number | null>(null);
  const [iterationConfigData, setIterationConfigData] = useState<IterationConfigData | null>(null);
  const [loadingIterationConfig, setLoadingIterationConfig] = useState(false);
  const [iterationDiffError, setIterationDiffError] = useState<string | null>(null);

  // Start optimization dialog state
  const [startDialogOpen, setStartDialogOpen] = useState(false);

  // Extract existing optimization config for the start dialog
  const existingOptConfig = evalData?.optimization ? {
    goal_description: evalData.optimization.goal_description,
    limits: evalData.optimization.limits,
    directives: evalData.optimization.directives,
  } : undefined;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!evalData) {
    return (
      <div className="container max-w-6xl py-8">
        <Alert variant="destructive">
          <AlertDescription>Evaluation not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  const objectives = evalData.optimization?.objectives || [];
  const isMultiObjective = objectives.length >= 2;
  const status = optStatus?.status || 'not_started';
  const primaryObjective = objectives[0]?.name || 'quality';

  // Calculate improvement from baseline using primary objective
  const getImprovement = () => {
    if (!optStatus?.baseline_values || !optStatus?.current_best_values) {
      return null;
    }
    const baselineScore = optStatus.baseline_values[primaryObjective];
    const bestScore = optStatus.current_best_values[primaryObjective];
    if (baselineScore === undefined || baselineScore === 0) return null;
    return ((bestScore - baselineScore) / baselineScore) * 100;
  };

  const improvement = getImprovement();

  const handleCheckpointAction = async () => {
    setCheckpointDialogOpen(false);
    setSelectedCheckpointId(null);
    await fetchOptimizationData();
  };

  // Fetch best config when dialog opens
  useEffect(() => {
    if (applyDialogOpen && !bestConfigData && !loadingBestConfig) {
      const fetchBestConfig = async () => {
        setLoadingBestConfig(true);
        setApplyError(null);
        try {
          const res = await fetch(`/api/evals/${evalId}/optimization/best-config`, {
            headers: getAuthHeaders(),
            credentials: 'include',
          });
          if (!res.ok) {
            const data = await res.json();
            throw new Error(data.detail || 'Failed to fetch best config');
          }
          const data = await res.json();
          setBestConfigData(data);
        } catch (err) {
          setApplyError(err instanceof Error ? err.message : 'Failed to fetch config');
        } finally {
          setLoadingBestConfig(false);
        }
      };
      fetchBestConfig();
    }
  }, [applyDialogOpen, evalId, bestConfigData, loadingBestConfig]);

  // Reset state when dialog closes
  const handleCloseApplyDialog = () => {
    setApplyDialogOpen(false);
    setApplyError(null);
    setApplySuccess(false);
    // Don't reset bestConfigData to avoid re-fetching if reopened
  };

  // Fetch iteration config when iteration diff dialog opens
  const handleViewIterationDiff = (iterationNum: number) => {
    setSelectedIteration(iterationNum);
    setIterationConfigData(null);
    setIterationDiffError(null);
    setIterationDiffOpen(true);
  };

  useEffect(() => {
    if (iterationDiffOpen && selectedIteration !== null && !iterationConfigData && !loadingIterationConfig) {
      const fetchIterationConfig = async () => {
        setLoadingIterationConfig(true);
        setIterationDiffError(null);
        try {
          const runIdParam = selectedRunId ? `?run_id=${selectedRunId}` : '';
          const res = await fetch(`/api/evals/${evalId}/optimization/iterations/${selectedIteration}/config${runIdParam}`, {
            headers: getAuthHeaders(),
            credentials: 'include',
          });
          if (!res.ok) {
            const data = await res.json();
            throw new Error(data.detail || 'Failed to fetch iteration config');
          }
          const data = await res.json();
          setIterationConfigData(data);
        } catch (err) {
          setIterationDiffError(err instanceof Error ? err.message : 'Failed to fetch config');
        } finally {
          setLoadingIterationConfig(false);
        }
      };
      fetchIterationConfig();
    }
  }, [iterationDiffOpen, selectedIteration, evalId, selectedRunId, iterationConfigData, loadingIterationConfig]);

  const handleCloseIterationDiff = () => {
    setIterationDiffOpen(false);
    setSelectedIteration(null);
    setIterationConfigData(null);
    setIterationDiffError(null);
  };

  const handleApplyConfig = async (createVersion: boolean) => {
    setApplyingConfig(true);
    setApplyError(null);
    try {
      const res = await fetch(`/api/evals/${evalId}/optimization/apply`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          create_version: createVersion,
          commit_message: `Applied optimization from iteration ${bestConfigData?.iteration}`,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to apply config');
      }
      setApplySuccess(true);
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : 'Failed to apply config');
    } finally {
      setApplyingConfig(false);
    }
  };

  return (
    <div className="container max-w-6xl py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/evals')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{evalData.name}</h1>
          <p className="text-muted-foreground">
            Iteratively improving {evalData.agent?.split('/').pop()?.replace('.md', '')}
          </p>
        </div>

        {/* Run History Selector */}
        {optimizationRuns.length > 1 && (
          <Select
            value={selectedRunId || optStatus?.run_id || ''}
            onValueChange={(value) => selectOptimizationRun(value || null)}
          >
            <SelectTrigger className="w-[280px]">
              <History className="h-4 w-4 mr-2 flex-shrink-0" />
              <SelectValue placeholder="Select run" />
            </SelectTrigger>
            <SelectContent>
              {optimizationRuns.map((run) => (
                <SelectItem key={run.run_id} value={run.run_id}>
                  <div className="flex items-center gap-2 flex-nowrap whitespace-nowrap">
                    {run.is_current && (
                      <Badge variant="secondary" className="text-xs px-1 flex-shrink-0">Current</Badge>
                    )}
                    <span className="flex-shrink-0">
                      {run.started_at
                        ? new Date(run.started_at).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : 'Unknown date'}
                    </span>
                    {run.improvement_percent !== undefined && run.improvement_percent !== null && (
                      <span className={`flex-shrink-0 ${run.improvement_percent > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {run.improvement_percent > 0 ? '+' : ''}{run.improvement_percent.toFixed(1)}%
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <div className="flex gap-2">
          {renderActionButtons(
            status,
            startingOptimization,
            () => setStartDialogOpen(true),
            handlePauseOptimization,
            handleResumeOptimization,
            optStatus?.pending_checkpoint_id
          )}
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Historical Run Banner */}
      {selectedRunId && !optimizationRuns.find(r => r.run_id === selectedRunId)?.is_current && (
        <Alert className="mb-4 border-blue-500 bg-blue-50 dark:bg-blue-950/30">
          <History className="h-4 w-4 text-blue-600" />
          <AlertDescription className="flex items-center justify-between">
            <span className="text-blue-700 dark:text-blue-300">
              Viewing historical optimization run from{' '}
              {optimizationRuns.find(r => r.run_id === selectedRunId)?.started_at
                ? new Date(optimizationRuns.find(r => r.run_id === selectedRunId)!.started_at!).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : 'unknown date'}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => selectOptimizationRun(null)}
            >
              View Current Run
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Checkpoint Banner */}
      {optStatus?.pending_checkpoint_id && (
        <Alert className="mb-6 border-amber-500 bg-amber-50 dark:bg-amber-950/30">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="flex items-center justify-between">
            <div>
              <strong className="text-amber-800 dark:text-amber-200">
                Checkpoint Review Needed
              </strong>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Iteration {optStatus.current_iteration} reached.
                {improvement !== null && (
                  <> Quality improved {improvement > 0 ? '+' : ''}{improvement.toFixed(1)}% from baseline.</>
                )}
              </p>
            </div>
            <Button
              onClick={() => {
                setSelectedCheckpointId(optStatus.pending_checkpoint_id!);
                setCheckpointDialogOpen(true);
              }}
            >
              Review Now
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Progress Overview */}
      {status !== 'not_started' && optStatus && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Optimization Progress</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {/* Score Card */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm capitalize">{primaryObjective} Score</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Best Score */}
                  <div>
                    <div className="flex justify-between items-baseline mb-1">
                      <span className="text-2xl font-bold">
                        {optStatus.current_best_values[primaryObjective]?.toFixed(2) || 'N/A'}
                      </span>
                      {improvement !== null && (
                        <Badge
                          className={
                            improvement > 0
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-red-100 text-red-800'
                          }
                        >
                          {improvement > 0 ? '+' : ''}
                          {improvement.toFixed(1)}% vs baseline
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Best (iteration {optStatus.best_iteration ?? 'N/A'})
                    </p>
                  </div>
                  {/* Baseline */}
                  <div className="text-sm text-muted-foreground">
                    Baseline:{' '}
                    <span className="font-mono">
                      {optStatus.baseline_values[primaryObjective]?.toFixed(2) || 'N/A'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Iteration Progress */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Iteration Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>
                        {optStatus.current_iteration} / {optStatus.max_iterations}
                      </span>
                      <span>
                        {Math.round(
                          (optStatus.current_iteration / optStatus.max_iterations) * 100
                        )}
                        %
                      </span>
                    </div>
                    <Progress
                      value={
                        (optStatus.current_iteration / optStatus.max_iterations) * 100
                      }
                    />
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Total Eval Runs: {optStatus.total_eval_runs}</span>
                    <span>Status: {status}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Directives Editor - only show when optimization is active */}
      {status !== 'not_started' && status !== 'completed' && (
        <section className="mb-8">
          <DirectivesEditor
            evalId={evalId}
            disabled={status === 'completed'}
          />
        </section>
      )}

      {/* Progress Chart */}
      {iterations.length > 0 && (
        <section className="mb-8">
          {isMultiObjective && pareto.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ParetoChart
                pareto={pareto}
                iterations={iterations}
                xObjective={objectives[0].name}
                yObjective={objectives[1].name}
                xDirection={objectives[0].direction as 'maximize' | 'minimize'}
                yDirection={objectives[1].direction as 'maximize' | 'minimize'}
                width={400}
                height={300}
              />
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Multi-Objective Optimization</CardTitle>
                  <CardDescription>
                    {pareto.length} solutions on Pareto frontier
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Optimizing for: {objectives.map((o) => o.name).join(', ')}
                  </p>
                </CardContent>
              </Card>
            </div>
          ) : objectives.length === 1 ? (
            <OptimizationProgressChart
              iterations={iterations}
              objective={objectives[0].name}
              direction={objectives[0].direction as 'maximize' | 'minimize'}
              width={600}
              height={250}
            />
          ) : null}
        </section>
      )}

      {/* Iteration History */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-4">Iteration History</h2>
        {iterations.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <p>No iterations yet.</p>
              <p className="text-sm mt-1">
                Start the optimization to see iteration history.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">#</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Changes</TableHead>
                  <TableHead className="w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {iterations
                  .slice()
                  .sort((a, b) => b.iteration - a.iteration)
                  .map((iter) => (
                    <IterationRow
                      key={iter.iteration}
                      iteration={iter}
                      isBest={iter.iteration === optStatus?.best_iteration}
                      isBaseline={iter.iteration === 0}
                      objectiveNames={objectives.map((o) => o.name)}
                      evalId={evalId}
                      onViewDiff={handleViewIterationDiff}
                    />
                  ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </section>

      {/* Current Best Config */}
      {optStatus?.best_iteration !== undefined && optStatus.best_iteration > 0 && (
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Current Best Configuration</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setApplyDialogOpen(true)}>
                <Eye className="h-4 w-4 mr-1" />
                View Diff
              </Button>
              <Button size="sm" onClick={() => setApplyDialogOpen(true)}>
                <ExternalLink className="h-4 w-4 mr-1" />
                Apply to Agent
              </Button>
            </div>
          </div>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    From iteration {optStatus.best_iteration}
                  </p>
                  {improvement !== null && (
                    <p
                      className={`text-sm ${
                        improvement > 0 ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {improvement > 0 ? '+' : ''}
                      {improvement.toFixed(1)}% improvement from baseline
                    </p>
                  )}
                </div>
                <Badge variant="outline">
                  <FileCode className="h-3 w-3 mr-1" />
                  Prompt optimized
                </Badge>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Configuration (collapsed) */}
      <Collapsible open={configOpen} onOpenChange={setConfigOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="flex items-center gap-2 p-0 mb-4">
            <h2 className="text-lg font-semibold">Configuration</h2>
            {configOpen ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Agent</p>
                  <p className="font-medium">
                    {evalData.agent?.split('/').pop()?.replace('.md', '') ||
                      'Not set'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Max Iterations</p>
                  <p className="font-medium">{optStatus?.max_iterations || 10}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Exploration Mode</p>
                  <p className="font-medium">{optStatus?.exploration_mode || 'sequential'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Objectives</p>
                  <p className="font-medium">{objectives.length}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Objectives</p>
                <div className="flex flex-wrap gap-2">
                  {objectives.map((obj, idx) => (
                    <Badge key={idx} variant="outline">
                      {obj.name} ({obj.direction})
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>

      {/* Checkpoint Review Dialog */}
      {selectedCheckpointId && (
        <CheckpointReview
          evalId={evalId}
          checkpointId={selectedCheckpointId}
          open={checkpointDialogOpen}
          onClose={() => {
            setCheckpointDialogOpen(false);
            setSelectedCheckpointId(null);
          }}
          onAction={handleCheckpointAction}
        />
      )}

      {/* Apply to Agent Dialog with Diff View */}
      <Dialog open={applyDialogOpen} onOpenChange={handleCloseApplyDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="h-5 w-5" />
              Apply Best Configuration
            </DialogTitle>
            <DialogDescription>
              {applySuccess ? (
                'Configuration applied successfully!'
              ) : (
                <>
                  Review changes from iteration {bestConfigData?.iteration || optStatus?.best_iteration}
                  {bestConfigData?.improvement_percent !== null && bestConfigData?.improvement_percent !== undefined && (
                    <Badge
                      className={`ml-2 ${
                        bestConfigData.improvement_percent > 0
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : ''
                      }`}
                    >
                      {bestConfigData.improvement_percent > 0 ? '+' : ''}
                      {bestConfigData.improvement_percent.toFixed(1)}% improvement
                    </Badge>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {applySuccess ? (
            <div className="flex-1 flex flex-col items-center justify-center py-8">
              <CheckCircle2 className="h-16 w-16 text-green-500 mb-4" />
              <p className="text-lg font-medium mb-2">Configuration Applied!</p>
              <p className="text-sm text-muted-foreground text-center mb-4">
                Your agent has been updated with the optimized configuration from iteration{' '}
                {bestConfigData?.iteration}.
              </p>
              <Button onClick={handleCloseApplyDialog}>Done</Button>
            </div>
          ) : loadingBestConfig ? (
            <div className="flex-1 flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2">Loading configuration diff...</span>
            </div>
          ) : applyError && !bestConfigData ? (
            <div className="flex-1 flex flex-col items-center justify-center py-8">
              <Alert variant="destructive" className="max-w-md">
                <AlertDescription>{applyError}</AlertDescription>
              </Alert>
              <Button
                variant="outline"
                className="mt-4"
                onClick={handleCloseApplyDialog}
              >
                Close
              </Button>
            </div>
          ) : bestConfigData ? (
            <>
              {/* Word-level diff summary */}
              <TextDiffSummary
                original={bestConfigData.current_agent_content || ''}
                modified={bestConfigData.optimized_agent_content || ''}
                showInlineDiff={true}
                maxInlineLength={3000}
                className="mb-3"
              />

              {/* Diff Editor */}
              <div className="flex-1 min-h-0 border rounded-md overflow-hidden">
                <DiffEditor
                  height="400px"
                  language="markdown"
                  original={bestConfigData.current_agent_content || '# No current agent content'}
                  modified={bestConfigData.optimized_agent_content || '# No optimized content'}
                  options={{
                    readOnly: true,
                    renderSideBySide: true,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    wordWrap: 'on',
                    fontSize: 13,
                  }}
                  theme="vs-dark"
                />
              </div>

              {/* Legend */}
              <div className="flex gap-4 text-xs text-muted-foreground mt-2">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-red-200 dark:bg-red-900 rounded" />
                  Removed
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-green-200 dark:bg-green-900 rounded" />
                  Added
                </span>
              </div>

              {applyError && (
                <Alert variant="destructive" className="mt-2">
                  <AlertDescription>{applyError}</AlertDescription>
                </Alert>
              )}

              {/* Actions */}
              <div className="flex justify-between items-center mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  This will update your agent with the optimized prompt.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleCloseApplyDialog}
                    disabled={applyingConfig}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleApplyConfig(false)}
                    disabled={applyingConfig}
                  >
                    {applyingConfig ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : null}
                    Save as Draft
                  </Button>
                  <Button
                    onClick={() => handleApplyConfig(true)}
                    disabled={applyingConfig}
                  >
                    {applyingConfig ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : null}
                    Apply & Create Version
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Iteration Diff Dialog */}
      <Dialog open={iterationDiffOpen} onOpenChange={handleCloseIterationDiff}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="h-5 w-5" />
              {iterationConfigData?.is_baseline ? (
                'Baseline Configuration'
              ) : (
                <>
                  Iteration {selectedIteration} Changes
                  {iterationConfigData?.is_best && (
                    <Badge className="ml-2 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      <Star className="h-3 w-3 mr-1" />
                      Best
                    </Badge>
                  )}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {iterationConfigData?.is_baseline ? (
                'Original agent configuration before optimization'
              ) : (
                <>
                  Changes from baseline
                  {iterationConfigData?.improvement_from_baseline !== null &&
                    iterationConfigData?.improvement_from_baseline !== undefined && (
                      <Badge
                        className={`ml-2 ${
                          iterationConfigData.improvement_from_baseline > 0
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : iterationConfigData.improvement_from_baseline < 0
                            ? 'bg-red-100 text-red-800'
                            : ''
                        }`}
                      >
                        {iterationConfigData.improvement_from_baseline > 0 ? '+' : ''}
                        {iterationConfigData.improvement_from_baseline.toFixed(1)}% from baseline
                      </Badge>
                    )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {loadingIterationConfig ? (
            <div className="flex-1 flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2">Loading configuration...</span>
            </div>
          ) : iterationDiffError ? (
            <div className="flex-1 flex flex-col items-center justify-center py-8">
              <Alert variant="destructive" className="max-w-md">
                <AlertDescription>{iterationDiffError}</AlertDescription>
              </Alert>
              <Button variant="outline" className="mt-4" onClick={handleCloseIterationDiff}>
                Close
              </Button>
            </div>
          ) : iterationConfigData ? (
            <>
              {/* Word-level diff summary (only for non-baseline iterations) */}
              {!iterationConfigData.is_baseline && (
                <TextDiffSummary
                  original={iterationConfigData.baseline_content || ''}
                  modified={iterationConfigData.iteration_content || ''}
                  showInlineDiff={true}
                  maxInlineLength={3000}
                  className="mb-3"
                />
              )}

              {/* Diff Editor or single view for baseline */}
              <div className="flex-1 min-h-0 border rounded-md overflow-hidden">
                {iterationConfigData.is_baseline ? (
                  // For baseline, show single editor (no diff)
                  <DiffEditor
                    height="400px"
                    language="markdown"
                    original={iterationConfigData.baseline_content || '# No content'}
                    modified={iterationConfigData.baseline_content || '# No content'}
                    options={{
                      readOnly: true,
                      renderSideBySide: false,
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      wordWrap: 'on',
                      fontSize: 13,
                    }}
                    theme="vs-dark"
                  />
                ) : (
                  // For other iterations, show diff from baseline
                  <DiffEditor
                    height="400px"
                    language="markdown"
                    original={iterationConfigData.baseline_content || '# No baseline content'}
                    modified={iterationConfigData.iteration_content || '# No iteration content'}
                    options={{
                      readOnly: true,
                      renderSideBySide: true,
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      wordWrap: 'on',
                      fontSize: 13,
                    }}
                    theme="vs-dark"
                  />
                )}
              </div>

              {/* Legend */}
              {!iterationConfigData.is_baseline && (
                <div className="flex gap-4 text-xs text-muted-foreground mt-2">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-red-200 dark:bg-red-900 rounded" />
                    Removed from baseline
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-green-200 dark:bg-green-900 rounded" />
                    Added in this iteration
                  </span>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-between items-center mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  {iterationConfigData.is_best ? (
                    <span className="text-green-600 dark:text-green-400 font-medium">
                      This is the best performing iteration
                    </span>
                  ) : iterationConfigData.is_baseline ? (
                    'Original configuration before any optimization'
                  ) : (
                    `Iteration ${selectedIteration} of the optimization run`
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleCloseIterationDiff}>
                    Close
                  </Button>
                  {iterationConfigData.is_best && (
                    <Button
                      onClick={() => {
                        handleCloseIterationDiff();
                        setApplyDialogOpen(true);
                      }}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Apply to Agent
                    </Button>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Start Optimization - opens full eval wizard in start mode */}
      <EvalWizard
        open={startDialogOpen}
        onClose={() => setStartDialogOpen(false)}
        evalId={evalId}
        editMode={true}
        startOptimizationMode={true}
        initialData={evalData}
        onSuccess={() => {
          fetchOptimizationData();
          hook.fetchData();
        }}
      />
    </div>
  );
}

// Helper components

function renderActionButtons(
  status: string,
  starting: boolean,
  onStart: () => void,
  onPause: () => void,
  onResume: () => void,
  pendingCheckpointId?: string
) {
  switch (status) {
    case 'not_started':
      return (
        <Button onClick={onStart} disabled={starting}>
          {starting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Play className="h-4 w-4 mr-2" />
          )}
          Start Optimization
        </Button>
      );
    case 'running':
      return (
        <Button variant="outline" onClick={onPause}>
          <Pause className="h-4 w-4 mr-2" />
          Pause
        </Button>
      );
    case 'paused':
      if (pendingCheckpointId) {
        return null; // Show review button in banner instead
      }
      return (
        <Button onClick={onResume}>
          <RotateCcw className="h-4 w-4 mr-2" />
          Resume
        </Button>
      );
    case 'completed':
      return (
        <>
          <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
            Completed
          </Badge>
          <Button variant="outline" onClick={onStart} disabled={starting}>
            {starting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4 mr-2" />
            )}
            Start New
          </Button>
        </>
      );
    case 'failed':
      return (
        <>
          <Badge variant="destructive">
            <XCircle className="h-3.5 w-3.5 mr-1" />
            Failed
          </Badge>
          <Button variant="outline" onClick={onStart}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Restart
          </Button>
        </>
      );
    default:
      return null;
  }
}

function IterationRow({
  iteration,
  isBest,
  isBaseline,
  objectiveNames,
  evalId,
  onViewDiff,
}: {
  iteration: OptimizationIteration;
  isBest: boolean;
  isBaseline: boolean;
  objectiveNames: string[];
  evalId: string;
  onViewDiff: (iteration: number) => void;
}) {
  const navigate = useNavigate();
  const score = objectiveNames.length > 0
    ? iteration.objective_values[objectiveNames[0]]
    : null;

  // Format proposed changes into a readable description
  const getChangesDescription = () => {
    if (isBaseline) return 'Original configuration';

    const changes = iteration.proposed_changes;
    if (!changes || changes.length === 0) {
      return 'Prompt modifications';
    }

    // Helper to get change type (handle both field names)
    const getChangeType = (c: typeof changes[0]) => c.change_type || c.type || 'modification';
    // Helper to get description (use rationale, field_path, or description)
    const getChangeDesc = (c: typeof changes[0]) => c.rationale || c.field_path || c.description;

    // Summarize the changes
    if (changes.length === 1) {
      const desc = getChangeDesc(changes[0]);
      const type = getChangeType(changes[0]);
      // Show field_path and type if available
      if (changes[0].field_path) {
        return `${changes[0].field_path} ${type}`;
      }
      return desc || type;
    }

    // Multiple changes - show count and field paths or types
    const fieldPaths = changes.map(c => c.field_path).filter(Boolean);
    if (fieldPaths.length > 0) {
      if (fieldPaths.length <= 2) {
        return `${changes.length} changes: ${fieldPaths.join(', ')}`;
      }
      return `${changes.length} changes (${fieldPaths.slice(0, 2).join(', ')}, ...)`;
    }

    const types = [...new Set(changes.map(c => getChangeType(c)))];
    if (types.length === 1) {
      return `${changes.length} ${types[0]} changes`;
    }
    return `${changes.length} changes (${types.slice(0, 2).join(', ')})`;
  };

  const handleViewRun = () => {
    if (iteration.eval_run_id) {
      navigate(`/evals/${evalId}/runs/${iteration.eval_run_id}`);
    }
  };

  return (
    <TableRow className={isBest ? 'bg-green-50 dark:bg-green-950/20' : ''}>
      <TableCell className="font-mono">
        {iteration.iteration}
        {isBest && (
          <Star className="h-3.5 w-3.5 text-amber-500 inline ml-1" />
        )}
      </TableCell>
      <TableCell>
        <span className="font-mono font-medium">
          {score?.toFixed(3) ?? 'N/A'}
        </span>
        {iteration.is_on_pareto && (
          <Badge variant="outline" className="ml-2 text-xs">
            Pareto
          </Badge>
        )}
      </TableCell>
      <TableCell>
        {isBaseline ? (
          <Badge variant="secondary">Baseline</Badge>
        ) : isBest ? (
          <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            Best
          </Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground max-w-[300px] truncate">
        {getChangesDescription()}
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button
            variant={isBest ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewDiff(iteration.iteration)}
            className={`h-7 px-2 text-xs ${isBest ? 'bg-green-600 hover:bg-green-700 text-white' : ''}`}
            title="View configuration diff from baseline"
          >
            <GitCompare className="h-3.5 w-3.5 mr-1" />
            Diff
          </Button>
          {iteration.eval_run_id && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewRun}
              className="h-7 px-2 text-xs"
              title="View eval run results"
            >
              <Eye className="h-3.5 w-3.5 mr-1" />
              Run
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

export default OptimizationEvalDetail;
